import { DeleteOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, Radio, Space, Typography } from 'antd';
import type { Rule } from 'antd/lib/form';
import React from 'react';

import type { VenueEditFormValues } from '../../../../../../types';
import { checkTimeOverlap } from '../../helpers';

const { Item, List, useFormInstance, useWatch } = Form;
const { Text } = Typography;

type OpeningHourFieldProps = {
  dayIndex: number;
  initialData: VenueEditFormValues['operatingHours'][0];
};

export const OpeningHourField = ({
  dayIndex,
  initialData,
}: OpeningHourFieldProps) => {
  const form = useFormInstance<VenueEditFormValues>();

  const statusFieldKey = ['operatingHours', dayIndex, 'status'];
  const timesFieldKey = ['operatingHours', dayIndex, 'times'];

  const openingStatus = useWatch(statusFieldKey, form);

  const handleStatusChange = (status: string): void => {
    if (status === 'allDay' || status === 'closed') {
      form.setFieldValue(timesFieldKey, initialData.times);
    }
  };

  const validateTimeSlot = (
    timeIndex: number,
    fieldType: 'startTime' | 'endTime'
  ) => {
    return {
      validator: async (_rule: Rule, value: number | null) => {
        try {
          if (value === null || value === undefined) {
            return Promise.reject(
              `請輸入${fieldType === 'startTime' ? '開始' : '結束'}時間`
            );
          }

          const times: VenueEditFormValues['operatingHours'][0]['times'] =
            form.getFieldValue(timesFieldKey) || [];

          const currentTime = times[timeIndex];

          if (fieldType === 'endTime' && currentTime?.startTime !== null) {
            if (value <= currentTime.startTime) {
              return Promise.reject('結束時間必須大於開始時間');
            }
          }

          if (fieldType === 'startTime' && currentTime?.endTime !== null) {
            if (currentTime.endTime <= value) {
              return Promise.reject('開始時間必須小於結束時間');
            }
          }

          const { hasOverlap, overlappingPairs } = checkTimeOverlap(times);

          if (hasOverlap) {
            for (const pair of overlappingPairs) {
              if (
                (fieldType === 'endTime' && timeIndex === pair.prevIndex) ||
                (fieldType === 'startTime' && timeIndex === pair.nextIndex)
              ) {
                return Promise.reject('與其他時間段重疊，請檢查');
              }
            }
          }

          const relatedFieldType =
            fieldType === 'startTime' ? 'endTime' : 'startTime';
          const relatedFieldName = [
            'operatingHours',
            dayIndex,
            'times',
            timeIndex,
            relatedFieldType,
          ];

          setTimeout(() => {
            form.validateFields([relatedFieldName]).catch(() => {});
          }, 0);

          return Promise.resolve();
        } catch {
          // do nothing
        }
      },
    };
  };

  return (
    <Space direction="vertical" className="w-full">
      <Item name={statusFieldKey} noStyle initialValue={openingStatus}>
        <Radio.Group onChange={(e) => handleStatusChange(e.target.value)}>
          <Radio value="open">開放</Radio>
          <Radio value="allDay">開放24小時</Radio>
          <Radio value="closed">非開放</Radio>
        </Radio.Group>
      </Item>

      {openingStatus === 'open' && (
        <List
          name={timesFieldKey}
          rules={[
            {
              validator: (
                _,
                allTimeRanges: VenueEditFormValues['operatingHours'][0]['times']
              ) => {
                const isTimeListEmpty =
                  !allTimeRanges || allTimeRanges.length < 1;
                if (isTimeListEmpty) {
                  return Promise.reject('請填寫開放時間');
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => {
            return (
              <Space direction="vertical" className="w-full">
                {fields.map(({ key, name: openingHourIndex, ...restField }) => (
                  <Space key={key} align="baseline">
                    <Space align="baseline">
                      <Text>開始時間：</Text>
                      <Item
                        {...restField}
                        name={[openingHourIndex, 'startTime']}
                        rules={[
                          validateTimeSlot(openingHourIndex, 'startTime'),
                        ]}
                      >
                        <InputNumber className="w-full" min={0} max={23} />
                      </Item>
                    </Space>

                    <Space align="baseline">
                      <Text>結束時間：</Text>
                      <Item
                        {...restField}
                        name={[openingHourIndex, 'endTime']}
                        rules={[validateTimeSlot(openingHourIndex, 'endTime')]}
                      >
                        <InputNumber className="w-full" min={1} max={24} />
                      </Item>
                    </Space>

                    {fields.length > 1 && (
                      <DeleteOutlined
                        rev={undefined}
                        onClick={() => remove(openingHourIndex)}
                      />
                    )}
                  </Space>
                ))}

                <Space direction="vertical" size={0} className="w-full">
                  <Space>
                    <Button
                      onClick={() => add({ startTime: null, endTime: null })}
                    >
                      新增開放時間
                    </Button>
                    <Text type="secondary">
                      時間為24小時制，請輸入0-24的數字
                    </Text>
                  </Space>
                  {errors[0] && <Text type="danger">{errors[0]}</Text>}
                </Space>
              </Space>
            );
          }}
        </List>
      )}
    </Space>
  );
};
